import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/shared";
import { GoogleButton, PinInput } from "@/components/GoogleButton";
import { api, errMsg, saveToken } from "@/lib/api";
import { Wrench, Warehouse, Wallet, Store, MessageCircle, Bot, KeyRound } from "lucide-react";

const FEATURES = [
  [Wrench, "Kasir", "Transaksi penjualan & jasa"], [Warehouse, "Gudang", "Stok, pembelian & supplier"], [Wallet, "Keuangan", "Laporan & arus kas"],
  [Store, "Marketplace", "Shopee, TikTok Shop"], [MessageCircle, "WhatsApp Business", "Chat, broadcast & customer service"], [Bot, "AI Assistant", "Analisis, rekomendasi & notifikasi"],
];

function PinLoginForm() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login-pin", { username, pin });
      saveToken(data);
      setUser(data);
      navigate("/app", { replace: true });
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="pin-login-form">
      <input data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username atau email" autoComplete="username"
        className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 outline-none rounded-md px-3 py-2.5" />
      <PinInput value={pin} onChange={setPin} testId="login-pin-input" placeholder="Password / PIN" />
      {err && <div className="text-sm text-red-300 bg-red-950/60 border border-red-900 rounded p-2" data-testid="pin-login-error">{err}</div>}
      <button type="submit" disabled={busy || !username || pin.length < 4} data-testid="pin-login-submit"
        className="w-full bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-md py-3 transition-colors flex items-center justify-center gap-2">
        <KeyRound className="h-4 w-4" />{busy ? "Memeriksa…" : "Masuk"}
      </button>
    </form>
  );
}

export default function Login() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("pin");
  if (loading) return <div className="min-h-screen mjm-bg"><Spinner /></div>;
  if (user) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen mjm-bg mjm-grain grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute -right-32 -top-32 h-[520px] w-[520px] rounded-full bg-red-700/20 blur-3xl" />
        <img src="/logo.png" alt="MJM Workshop" className="h-16 w-fit relative" />
        <div className="relative">
          <div className="eyebrow mb-3">Satu sistem, semua kebutuhan bengkel</div>
          <h1 className="font-display text-6xl font-black leading-[0.95] text-white">LEBIH CEPAT<br />LEBIH RAPI<br /><span className="text-red-500">LEBIH PROFIT</span></h1>
          <p className="text-zinc-400 mt-6 max-w-md">Input sekali, mengalir ke mana-mana: antrean, mekanik, gudang, kasir, keuangan, komisi, tracking pelanggan.</p>
          <div className="grid grid-cols-2 gap-3 mt-10 max-w-lg">
            {FEATURES.map(([Icon, t, d]) => (
              <div key={t} className="flex gap-3 items-start panel p-3">
                <div className="h-8 w-8 rounded bg-red-900/50 border border-red-800/60 flex items-center justify-center"><Icon className="h-4 w-4 text-red-400" /></div>
                <div><div className="text-sm font-semibold">{t}</div><div className="text-[11px] text-zinc-500">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-zinc-600 font-mono">MJM WORKSHOP · BUILD SMARTER, RUN FASTER</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm panel-red p-8 fade-up" data-testid="login-card">
          <img src="/logo.png" alt="MJM Workshop" className="h-14 mx-auto mb-8 lg:hidden" />
          <div className="eyebrow">Selamat datang</div>
          <h2 className="font-display text-3xl font-bold mt-1 mb-5">Masuk ke MJM Workshop</h2>
          {params.get("error") && <div className="mb-4 text-sm text-red-300 bg-red-950/60 border border-red-900 rounded p-3" data-testid="login-error">Login gagal, silakan coba lagi.</div>}

          <div className="grid grid-cols-2 gap-1 p-1 bg-zinc-950 border border-zinc-800 rounded-md mb-5" data-testid="login-tabs">
            {[["pin", "Email / Username"], ["google", "Google"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTab(k)} data-testid={`login-tab-${k}`}
                className={`text-sm py-2 rounded transition-colors ${tab === k ? "bg-red-800 text-white font-semibold" : "text-zinc-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>

          {tab === "pin" ? <PinLoginForm /> : (
            <div className="space-y-3">
              <GoogleButton />
              <p className="text-[11px] text-zinc-500 text-center">Gunakan email yang didaftarkan Owner supaya akun langsung aktif.</p>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-zinc-800/80 text-center text-xs text-zinc-500">
            Staf baru? <Link to="/aktivasi" data-testid="go-activate-link" className="text-red-400 hover:text-red-300 font-semibold">Aktivasi dengan kode undangan</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
